import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/infrastructure/api/client";
import type { CreatePaymentDto, SettlementWithPaymentDto } from "@tabipla/shared";
import { ApiError, type PaymentConfirmationDto } from "@tabipla/shared";

// Fetch payments for a session
export function usePayments(groupId: string, sessionId: string) {
  return useQuery({
    queryKey: ["groups", groupId, "sessions", sessionId, "payments"],
    queryFn: async () => {
      const response = await apiClient.get<PaymentConfirmationDto[]>(
        `/api/groups/${groupId}/sessions/${sessionId}/payments`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch payments");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId,
  });
}

// Fetch settlements with payment status
export function useSettlementsWithPayments(groupId: string, sessionId: string) {
  return useQuery({
    queryKey: ["groups", groupId, "sessions", sessionId, "settlements-with-payments"],
    queryFn: async () => {
      const response = await apiClient.get<SettlementWithPaymentDto[]>(
        `/api/groups/${groupId}/sessions/${sessionId}/payments/settlements`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch settlements");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId,
  });
}

// Create a payment record
export function useCreatePayment(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePaymentDto) => {
      const response = await apiClient.post<PaymentConfirmationDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/payments`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create payment");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["groups", groupId, "sessions", sessionId, "payments"],
      });
      queryClient.invalidateQueries({
        queryKey: ["groups", groupId, "sessions", sessionId, "settlements-with-payments"],
      });
    },
  });
}

// Mark payment as paid (payer action)
export function useMarkPaymentPaid(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentId: string) => {
      const response = await apiClient.post<PaymentConfirmationDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/payments/${paymentId}/paid`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to mark as paid");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["groups", groupId, "sessions", sessionId, "payments"],
      });
      queryClient.invalidateQueries({
        queryKey: ["groups", groupId, "sessions", sessionId, "settlements-with-payments"],
      });
    },
  });
}

// Confirm payment received (receiver action)
export function useConfirmPayment(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentId: string) => {
      const response = await apiClient.post<PaymentConfirmationDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/payments/${paymentId}/confirm`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to confirm payment");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["groups", groupId, "sessions", sessionId, "payments"],
      });
      queryClient.invalidateQueries({
        queryKey: ["groups", groupId, "sessions", sessionId, "settlements-with-payments"],
      });
    },
  });
}

// Delete a payment record
export function useDeletePayment(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentId: string) => {
      await apiClient.delete(`/api/groups/${groupId}/sessions/${sessionId}/payments/${paymentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["groups", groupId, "sessions", sessionId, "payments"],
      });
      queryClient.invalidateQueries({
        queryKey: ["groups", groupId, "sessions", sessionId, "settlements-with-payments"],
      });
    },
  });
}
